import { state, shortUUID } from '@codesuma/baseline'
import { IDayCell } from './calendar'
import { nextSeq, sortBySeq } from './storage/rows'
import { collection } from './storage'

// The day's notes, persisted through the storage repository and broadcast on
// the baseline state bus.
//
// A day can hold as many as the user writes. It held exactly one for a long
// while, keyed by the date itself, and those rows are still here: an id of
// '2026-9-13' is as unique as a shortUUID, so an old note reads back as that
// day's first note and needs no migration. Only new ones get a generated id.
//
// Nothing subscribes to the 'notes' broadcast on purpose. The day slide owns
// the field the user is typing into, so refilling it from the bus would take
// the caret away mid-sentence.

export interface INote {
    id: string
    text: string
    gy: number
    gm: number
    gd: number
    // Insertion order, the same stamp the task and event stores carry — the
    // engine returns key order and ids are random. Absent on the one-per-day
    // rows, which sorted by nothing because there was never more than one.
    seq?: number
}

const KEY = 'notes'
const repo = collection<INote>(KEY)
let all: INote[] = []

export async function init() {
    all = sortBySeq(await repo.load())
}

// In the order they were written, so a day that has been added to over an
// evening reads down the page the way it happened.
function forDay(cell: IDayCell): INote[] {
    return all.filter(n => n.gy === cell.gy && n.gm === cell.gm && n.gd === cell.gd)
}

function byId(id: string): INote | undefined {
    return all.find(n => n.id === id)
}

function broadcast() {
    state.set(KEY, all)
}

function add(cell: IDayCell, text: string): INote | undefined {
    const trimmed = text.trim()
    if (!trimmed) return
    const note: INote = {
        id: shortUUID(), text: trimmed,
        gy: cell.gy, gm: cell.gm, gd: cell.gd, seq: nextSeq(all),
    }
    all = [...all, note]
    void repo.put(note)
    broadcast()
    return note
}

// Emptied text drops the note rather than keeping a blank row, so one opened
// and thought better of leaves nothing behind.
function update(id: string, text: string) {
    const trimmed = text.trim()
    if (!trimmed) return remove(id)
    const found = all.find(n => n.id === id)
    if (!found || found.text === trimmed) return
    const saved = { ...found, text: trimmed }
    all = all.map(n => n.id === id ? saved : n)
    void repo.put(saved)
    broadcast()
}

function remove(id: string) {
    if (!all.some(n => n.id === id)) return
    all = all.filter(n => n.id !== id)
    void repo.remove(id)
    broadcast()
}

export default { init, forDay, byId, all: () => all, add, update, remove }
